import React, { useEffect, useState } from 'react';
import devices from 'devextreme/core/devices';
import themes from 'devextreme/ui/themes';
import cal from '@iatric/fastlane-client-app-library';
import routes from './routes';
import nav from './nav';
import {
  FastlaneAccessProvider,
  filterNavigationByAccess,
} from './services/fastlaneSecurity';
import { applyRuntimeConfigDefaults, hydrateRuntimeConfig } from './services/runtimeConfig';
import './App.scss';
import logoSm from './assets/img/brand/logoSm.png';
import logoLg from './assets/img/brand/logoLg.png';

const { DefaultLayout, auth, appService } = cal;

const timeoutPromise = (promise, timeoutMs) =>
  Promise.race([
    promise,
    new Promise((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Timed out after ${timeoutMs}ms`));
      }, timeoutMs);
    }),
  ]);

// Ensure Fastlane config keys exist before first render.
applyRuntimeConfigDefaults();

function App() {
  const [renderReady, setRenderReady] = useState(false);
  const [navigation, setNavigation] = useState(() => {
    window.config.device = devices.current();
    return nav;
  });
  const [authenticatedUser, setAuthenticatedUser] = useState(null);

  useEffect(() => {
    const initialize = async () => {
      await hydrateRuntimeConfig();

      let nextAuthenticatedUser = null;
      const { main, data, advanced, report } = navigation;
      let nextNavigation = { main, data, advanced, report };

      try {
        const user = await timeoutPromise(auth.getCurrentUser(), 8000);
        if (user) nextAuthenticatedUser = user;
        const {
          main: refreshedMain,
          data: refreshedData,
          advanced: refreshedAdvanced,
          report: refreshedReport,
        } = navigation;
        nextNavigation = {
          main: refreshedMain,
          data: refreshedData,
          advanced: refreshedAdvanced,
          report: refreshedReport,
        };
      } catch (_error) {
        // Continue with unauthenticated defaults when user bootstrap fails or times out.
      }

      try {
        nextNavigation = await timeoutPromise(appService.initializeApp(nextNavigation), 8000);
      } catch (_error) {
        // Preserve the locally built navigation when Fastlane app initialization fails.
      }

      setAuthenticatedUser(nextAuthenticatedUser);
      setNavigation(filterNavigationByAccess(nextNavigation, nextAuthenticatedUser));
      setRenderReady(true);
    };

    initialize();
    // navigation is intentionally captured from initial state to match mount-only class behavior.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FastlaneAccessProvider authenticatedUser={authenticatedUser}>
      <DefaultLayout
        authenticatedUser={authenticatedUser}
        navigation={navigation}
        routes={routes}
        logoSm={logoSm}
        logoLg={logoLg}
        renderReady={renderReady}
        themes={themes}
      />
    </FastlaneAccessProvider>
  );
}

export default App;
